import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { PropertyTable } from '../metadata/propertyConfig';
import { Columns } from '@/types/table';
import { TableRowActionOption } from '@/components/ui/table-row-actions';
import { Variants } from '@/types/common';
import { processData } from '../utils/propertyListTransform';
import { useSelectedEntityId } from '@/hooks/useEntityScope';
import {
  getOptionsForMoreView,
  getSortByString
} from '../utils/propertyListUtils';
import { useAddPropertiesToCart } from './useAddPropertiesToCart';
import { setCartCount } from '../slice/OrderCartSlice';
import { showAlert } from '../../../../slice/AlertSlice';
import { useNavigationService } from '../../../../services/navigate-service';
import { buildRoute } from '../../../../config/routes';
import {
  orderSortSurvivesView,
  resolveOrderView
} from '../../../orders/order-views/orderViews';
import { orderRowAssetsNav } from '../../../orders/orderRowAssetsNav';
import { PERSISTENCE_KEYS } from '../../../../util/persistedState';
import { ListingSort, readListingPaging, writeListingPaging } from '../listingPaging';
import useUnifiedSSE from '../../../../hooks/useUnifiedSSE';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { PropertyAddToCartCell } from '../cells/PropertyAddToCartCell';
import { RecentAssetsCell } from '../cells/RecentAssetsCell';
import { useEditCancelModal } from './useEditCancelModal';
import LIST_VIEW_COLUMNS from '../metadata/listViewColumns';
import ORDER_LIST_COLUMNS from '../metadata/orderListColumns';
import { useRowSelection } from '@/components/ui/hooks/use-row-selection';
import { useLazyGetPropertiesQuery } from '../../services/propertyApi';
import { canCreateOnRoute } from '../../../../util/permissions';
import { useLazyGetOrdersQuery } from '../../../orders/services/orderApi';

type ListPageResponse = {
  totalElements: number;
  totalElementsAfterFilter: number;
  content: Record<string, unknown>[];
};

// Hoisted so the cart-denied role (DV-2751) gets a STABLE columns identity
// across renders. Building `LIST_VIEW_COLUMNS.filter(...)` fresh inside
// getColumns() handed react-table a new array every render and forced it to
// recompute its column model — precisely for the role this change targets.
const LIST_VIEW_COLUMNS_WITHOUT_CART = LIST_VIEW_COLUMNS.filter(
  (c) => c.selector !== 'addToCart',
);

export function usePropertyListData(
  isProperty: boolean,
  onOpen?: (item: PropertyTable) => void,
) {
  const dispatch = useDispatch();
  // One gate shared by the per-row cart cell and the column set (DV-2751) so the
  // two can't disagree: when false, neither the addToCart column nor its per-row
  // PropertyAddToCartCell is built.
  const canAddToCart = canCreateOnRoute('Order');
  const { navigateTo } = useNavigationService();
  const [searchParams] = useSearchParams();
  const modal = useEditCancelModal();

  // Card / list view — list (table) is the default for property view;
  // existing users with a persisted `cardView` preference keep theirs.
  const [isCardView, setIsCardView] = useState<boolean>(() => {
    const view = JSON.parse(localStorage.getItem('cardView') || 'false');
    return isProperty && view === true;
  });

  useEffect(() => {
    if (isProperty) localStorage.setItem('cardView', JSON.stringify(isCardView));
    // Skip-initial-pagination gate is set synchronously in the toggle
    // click handler (see PropertyListPage.onActionClick), not here — a
    // useEffect would fire too late, after the new pagination's
    // mount-effect has already consumed the stale value.
  }, [isCardView, isProperty]);

  // Table state
  const [totalRows, setTotalRows] = useState(1);
  const [totalAvailableRows, setTotalAvailableRows] = useState(1);
  const [tableData, setTableData] = useState<PropertyTable[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isNoList, setIsNoList] = useState(false);
  const [isNoListFilter, setIsNoListFilter] = useState(false);
  // DV-3455: page, page size and sort survive leaving the list. Read once at
  // mount for the orders view on screen (`null` for Properties); the page
  // only comes back for the view it was recorded on. The page component
  // makes the first fetch from `restoredPaging` and seeds the table and
  // pagination UI from `page` / `activeSort`.
  const pagingKey = isProperty
    ? PERSISTENCE_KEYS.propertiesPaging
    : PERSISTENCE_KEYS.ordersPaging;
  const pagingView = isProperty ? null : resolveOrderView(searchParams.get('view')).key;
  const [restoredPaging] = useState(() => {
    const stored = readListingPaging(pagingKey, pagingView);
    return orderSortSurvivesView(stored.sort, pagingView) ? stored : { ...stored, sort: null };
  });
  const [page, setPage] = useState([0, 25]);
  // A completion-date sort is Completed's own (DV-3122 review): filtered out
  // of the restore above, and dropped below when the view changes.
  const [activeSort, setActiveSort] = useState<ListingSort | null>(restoredPaging.sort);
  // Read by onPageChange, which can run in the same tick as onSort (sort,
  // then refetch) — before the state update above has committed.
  const activeSortRef = useRef(activeSort);
  // In-render reset on a view switch: the hook stays mounted (only ?view=
  // changes), and usePropertyFilters' view-change refetch reads `sort` after
  // this render, so the drop must land before it. Same pattern as
  // useGlobalOrderFilters' view default.
  const lastPagingViewRef = useRef(pagingView);
  if (lastPagingViewRef.current !== pagingView) {
    lastPagingViewRef.current = pagingView;
    if (!orderSortSurvivesView(activeSort, pagingView)) {
      activeSortRef.current = null;
      setActiveSort(null);
    }
  }
  const sort = activeSort ? getSortByString(activeSort.selector, activeSort.asc) : '';
  const [tableRawData, setTableRawData] = useState<unknown>();
  const [addingPropertyId, setAddingPropertyId] = useState<string | null>(null);
  // Both listings migrated to RTK Query — cancellation is handled by .abort()
  // on the trigger promise the lazy hook returns.
  const [triggerProperties, propertiesResult] = useLazyGetPropertiesQuery();
  const [triggerOrders, ordersResult] = useLazyGetOrdersQuery();
  const activeResult = isProperty ? propertiesResult : ordersResult;
  const addPropertiesToCart = useAddPropertiesToCart();
  // Single ref for the in-flight trigger promise; aborted at the start of
  // every new request so a fast double-click / filter-change replaces the
  // previous fetch instead of stacking.
  const inFlightPromiseRef = useRef<{ abort: () => void } | null>(null);
  const inFlightTokenRef = useRef(0);
  const skipInitialPaginationRef = useRef(true);

  // SSE for live order status updates. Subscribed from the scope slice: a
  // mount-cached read would leave the stream pointed at the previous account
  // once a switch stops reloading the document (DV-3013 §2.1 class A).
  const selectedEntity = useSelectedEntityId() ?? '';
  useUnifiedSSE(selectedEntity, {
    onOrderStatusChange: (updatedOrder) => {
      setTableRawData((prev: unknown) => {
        const raw = prev as { content: Record<string, unknown>[] } | null;
        if (!raw?.content) return prev;
        const updated = raw.content.map((o) =>
          o.orderId === updatedOrder.orderId
            ? { ...o, orderStatus: updatedOrder.orderStatus,
                orderStatusUpdatedAt: updatedOrder.orderStatusUpdatedAt }
            : o
        );
        const next = { ...raw, content: updated };
        setTableData(appendRowActionCells(processData(next, isProperty, onOpen)));
        return next;
      });
    }
  });

  // Applies a list response to the table state. Fed exclusively by the
  // cache-subscription effect below — never by awaiting a trigger promise:
  // a trigger fired while a tag-invalidation refetch (e.g. createProperty
  // invalidating PropertyList) is already in flight for the same cache key
  // dedupes into it and can resolve from the stale cache snapshot. The
  // subscribed result state always reflects what actually landed last.
  const lastFilterStrRef = useRef('');
  // The last request's arguments, so a page that came back past the end of
  // the list can be re-asked for as page 1 (DV-3455).
  const lastRequestRef = useRef<{
    upperIndex: number; rowsPerPage: number;
    filterStr: string; sortQuery: string; queryParams: string;
  } | null>(null);
  const applyListResponse = (response: ListPageResponse) => {
    // A restored page (or a list that shrank under the user) can lie past the
    // end: rows exist, this page has none. Ask for page 1 instead of showing
    // "no results" for a list that has results.
    const last = lastRequestRef.current;
    const isPastTheEnd =
      response.content.length === 0 && response.totalElementsAfterFilter > 0;
    if (isPastTheEnd && last && last.upperIndex > 1) {
      void onPageChange(1, last.rowsPerPage, last.filterStr, last.sortQuery, last.queryParams);
      return;
    }
    // RTK Query deep-freezes query data with Immer in development. processData
    // (and the SSE handler) mutate row fields in place, so clone to a writable
    // copy before handing off. Cheap for typical page sizes.
    const d: ListPageResponse = structuredClone(response);
    setTotalRows(d.totalElements);
    if (d.totalElements === 0) {
      const stripped = lastFilterStrRef.current
        .replace(/&?entityUUIDs=[^&]*/g, '')
        .replace(/&?isActive=[^&]*/g, '')
        .replace(/&?dummyFilter=[^&]*/g, '')
        .replace(/^&/, '');
      setIsNoListFilter(stripped.length > 0);
      setIsNoList(stripped.length === 0);
    } else if (d.content.length === 0 || d.totalElementsAfterFilter === 0) {
      setIsNoListFilter(true);
      setIsNoList(false);
    } else {
      setIsNoListFilter(false);
      setIsNoList(false);
    }
    setTotalAvailableRows(d.totalElementsAfterFilter);
    setTableRawData(d);
    setTableData(appendRowActionCells(
      processData(d, isProperty, onOpen)
    ));
  };

  useEffect(() => {
    if (activeResult.data) {
      applyListResponse(activeResult.data as unknown as ListPageResponse);
    }
    // applyListResponse is recreated per render; keying on the cache entry's
    // data identity is what "new response landed" means here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeResult.data]);

  // Data fetcher — both lists via RTK Query lazy hooks. The trigger promise
  // is awaited only for error logging and the loader lifecycle; the response
  // itself arrives through the cache-subscription effect above.
  const onPageChange = async (
    upperIndex: number, rowsPerPage: number,
    filterStr = '', sortString = '', queryParams = '', _entity = ''
  ) => {
    inFlightPromiseRef.current?.abort();
    setIsLoading(true);
    setPage([upperIndex, rowsPerPage]);
    const sortQ = sortString || sort;
    const idx = upperIndex - 1 < 0 ? 0 : upperIndex - 1;
    const token = ++inFlightTokenRef.current;
    lastFilterStrRef.current = filterStr;
    lastRequestRef.current = {
      upperIndex, rowsPerPage, filterStr, sortQuery: sortQ, queryParams,
    };
    writeListingPaging(pagingKey, {
      view: pagingView,
      page: Math.max(upperIndex, 1),
      pageSize: rowsPerPage,
      sort: activeSortRef.current,
    });

    const queryArg = {
      page: idx,
      size: rowsPerPage,
      filterQuery: `${filterStr}${queryParams}`,
      sortQuery: sortQ,
    };

    const promise = isProperty
      ? triggerProperties(queryArg)
      : triggerOrders(queryArg);
    inFlightPromiseRef.current = promise;

    try {
      await promise.unwrap();
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== 'AbortError') {
        console.error('API Error:', err);
      }
    } finally {
      // Token guard: a newer request may have started while we were
      // suspended at the await; if so, leave isLoading true so its loader
      // keeps showing instead of clearing it prematurely.
      if (token === inFlightTokenRef.current) setIsLoading(false);
    }
  };

  // Row action helpers. Properties get the per-row Add to Cart + Recent Assets
  // cells (both read live state / navigate on click); Orders keep the 3-dot
  // Quick Actions menu.
  const appendRowActionCells = (data: PropertyTable[]): PropertyTable[] =>
    data.map((row) => {
      if (isProperty) {
        // DV-2751: only build the add-to-cart cell when the role can order —
        // getColumns drops the column on the same gate, so a gated role gets
        // neither the column nor a per-row cell allocated for it.
        if (canAddToCart) {
          row.addToCart = {
            component: <PropertyAddToCartCell row={row} />
          };
        }
        row.recentAssets = {
          component: (
            <RecentAssetsCell
              propertyId={row.id}
              hasPhotos={row.hasPhotos === 'Yes'}
              has3DTour={row.has3DTour === 'Yes'}
            />
          )
        };
      } else {
        row.moreOption = {
          component: (
            <TableRowActions data={row}
              options={getOptionsForMoreView(row.orderStatus)}
              title="Quick Actions" onMoreOptionClick={onMoreOptionClick} />
          )
        };
      }
      return row;
    });

  // Orders-only menu now: Properties moved Edit/Add-to-Cart/Manage-Assets to
  // per-row cells + the property-detail drawer.
  const onMoreOptionClick = (
    option: TableRowActionOption, rowData: PropertyTable
  ) => {
    if (option.text === 'Edit Order' || option.text === 'Cancel Order')
      modal.openEditCancelModal(option.text, rowData);
    else if (option.text === 'Manage Assets') navigateToAssets(rowData);
  };

  const handleAddToCart = (rowData: PropertyTable) => {
    if (addingPropertyId) return;
    setAddingPropertyId(rowData.id);
    addPropertiesToCart([rowData.id])
      .then(({ itemsCount, addedCount }) => {
        dispatch(setCartCount(itemsCount));
        if (addedCount > 0) {
          dispatch(showAlert({
            type: Variants.SUCCESS,
            message: '1 property successfully added to cart'
          }));
        }
      })
      .catch((e: unknown) => console.error('Error adding to cart', e))
      .finally(() => setAddingPropertyId(null));
  };

  const navigateToAssets = (rowData: PropertyTable) => {
    const raw = rowData.apiResponseRaw;
    if (!isProperty) {
      // DV-3521: the same entry as the Assets cell's Manage Assets
      // (OrderCellAssets), through one helper: the order's own photoset in
      // the route and the order in the query, so uploads are this order's
      // after a reload too, and the list's preset view for Back (DV-3455).
      const nav = orderRowAssetsNav(
        {
          propertyId: raw?.propertyId,
          orderId: raw?.orderId,
          photosetUUID: raw?.photosetUUID,
          formattedAddress: rowData.formattedAddress,
          property: raw?.property
        },
        searchParams.get('view')
      );
      navigateTo(nav.path, nav.query, nav.state);
      return;
    }
    // A property row opens no particular order: `orderUUID` here is the
    // property's latest order, a paint seed for the workspace's first grid
    // only (usePropertyAssets reads the opened order from the query).
    // Properties have no views.
    navigateTo(buildRoute.propertyAssets(rowData.id), { source: 'property' }, {
      propertyAddress: rowData.formattedAddress,
      propertyType: rowData.propertyType,
      propertySource: rowData.propertySource,
      hasPhotoset: rowData.hasPhotoset,
      orderUUID: raw?.orderId
    });
  };

  const openProperty = (ed: PropertyTable, intent: 'view' | 'edit' = 'view') =>
    navigateTo(
      buildRoute.propertyDetail(ed.id),
      {},
      intent === 'edit' ? { mode: 'edit' } : null,
    );

  const selection = useRowSelection(tableData);

  const onSort = (
    selector: string | null, asc: boolean | null,
    appendFilter: (f: unknown, p: number, s: number, ss?: string) => void
  ) => {
    if (selector && asc !== null) {
      const next = { selector, asc };
      activeSortRef.current = next;
      setActiveSort(next);
      appendFilter(null, page[0], page[1], getSortByString(selector, asc));
    }
  };

  const getColumns = (): Columns[] => {
    if (!isProperty) return ORDER_LIST_COLUMNS;
    // DV-2751: roles without cart access get no add-to-cart column at all —
    // keyed on the same `canAddToCart` gate as the per-row cell, and returning
    // the hoisted constant so the columns identity stays stable across renders.
    return canAddToCart ? LIST_VIEW_COLUMNS : LIST_VIEW_COLUMNS_WITHOUT_CART;
  };

  return {
    // isFetching covers refetches this hook didn't start (tag invalidation
    // after a create) so the loader shows for those too.
    isLoading: isLoading || activeResult.isFetching,
    tableData, totalRows, totalAvailableRows,
    page, setPage, sort, activeSort, restoredPaging, tableRawData,
    isNoList, setIsNoList, isNoListFilter,
    isCardView, setIsCardView,
    onPageChange, onSort, getColumns,
    openProperty, selection, handleAddToCart,
    onMoreOptionClick, addingPropertyId, skipInitialPaginationRef,
    ...modal
  };
}
